import React, { useState } from 'react';
import { CoreEntraIdUserExportPasswordSettings, EntraIdUserExportPasswordSettingType } from '../../../../../../models/CoreEntraIdUserModels';
import BoolInput from "../../../../../misc/BoolInput";
import EnumInput from "../../../../../misc/EnumInput";
import IntInput from "../../../../../misc/IntInput";
import TextInput from "../../../../../misc/TextInput";
import * as EntraId from './EntraIdUserTypes';
import { Button, Card, CardBody, CardHeader, Collapse } from 'reactstrap';
import { useTranslation } from 'react-i18next';

interface EntraIdUserUserPasswordSettingsProps {
    passwordSettings: CoreEntraIdUserExportPasswordSettings
    disabled?: boolean
    validation: string[]
    onChange: (x: CoreEntraIdUserExportPasswordSettings) => void
}

const EntraIdUserUserPasswordSettings: React.FC<EntraIdUserUserPasswordSettingsProps> = (props) => {
    const [isOpen, setOpen] = useState<boolean>(true);
    const { t } = useTranslation(['configuration', 'common']);


    const updateConfig = (change: Partial<CoreEntraIdUserExportPasswordSettings>) => {
        props.onChange({...props.passwordSettings, ...change})
    }

    const renderTypeContent = () => {
        switch (props.passwordSettings.type) {
            case EntraIdUserExportPasswordSettingType.STATIC: {
                return (<>
                    <hr />
                    <h5>{t('entraIdUserPasswordSettingTypeStatic.header', {ns: 'configuration'})}</h5>
                    <TextInput
                        title={t('entraIdUserPasswordSettingTypeStatic.passwordSettingsStaticValue.title', {ns: 'configuration'})}
                        name="passwordSettings.staticValue"
                        value={props.passwordSettings.staticValue}
                        disabled={props.disabled}
                        updateValue={(name, value) => updateConfig({ staticValue: value})}
                        invalidFeedback={t('entraIdUserPasswordSettingTypeStatic.passwordSettingsStaticValue.invalidFeedback', {ns: 'configuration'})}
                        invalid={props.validation.indexOf("passwordSettings.staticValue") !== -1}
                    />
                    <BoolInput
                        title={t('entraIdUserPasswordSettingTypeStatic.passwordSettingsUseMonthAndDayFromSocialnumber.title', {ns: 'configuration'})}
                        name="passwordSettings.useMonthAndDayFromSocialnumber"
                        value={props.passwordSettings.useMonthAndDayFromSocialnumber}
                        disabled={props.disabled}
                        updateValue={(name, value) => updateConfig({ useMonthAndDayFromSocialnumber: value })}
                        titleSmall={true}
                    />
                </>);
            }
            case EntraIdUserExportPasswordSettingType.RANDOM: {
                return (<>
                    <hr />
                    <h5>{t('entraIdUserPasswordSettingTypeRandom.header', {ns: 'configuration'})}</h5>
                    <IntInput
                        title={t('entraIdUserPasswordSettingTypeRandom.passwordSettingsMinLength.title', {ns: 'configuration'})}
                        name="passwordSettings.minLength"
                        disabled={props.disabled}
                        value={props.passwordSettings.minLength}
                        updateValue={(name, value) => updateConfig({ minLength: value })}
                        invalidFeedback={t('entraIdUserPasswordSettingTypeRandom.passwordSettingsMinLength.invalidFeedback', { ns: 'configuration' })}
                        invalid={props.validation.indexOf("passwordSettings.minLength") !== -1}
                    />
                    <IntInput
                        title={t('entraIdUserPasswordSettingTypeRandom.passwordSettingsMaxLength.title', {ns: 'configuration'})}
                        name="passwordSettings.maxLength"
                        disabled={props.disabled}
                        value={props.passwordSettings.maxLength}
                        updateValue={(name, value) => updateConfig({ maxLength: value })}
                        invalidFeedback={t('entraIdUserPasswordSettingTypeRandom.passwordSettingsMaxLength.invalidFeedback', { ns: 'configuration' })}
                        invalid={props.validation.indexOf("passwordSettings.maxLength") !== -1}
                    />
                    <EnumInput
                        title={t('entraIdUserPasswordSettingTypeRandom.passwordSettingsPasswordCharacterSets.title', {ns: 'configuration'})}
                        multi={true}
                        name="passwordSettings.passwordCharacterSets"
                        value={props.passwordSettings.passwordCharacterSets}
                        options={EntraId.passwordCharacterSetSelectionOptions.map(key => ({
                            value: key,
                            text: t(`entraIdUserPasswordSettingTypeRandom.passwordSettingsPasswordCharacterSets.options.${key}`, {ns: 'configuration'})
                        }))}
                        onChange={(name, value) => updateConfig({ passwordCharacterSets: value })}
                        disabled={props.disabled}
                        invalidFeedback={t('entraIdUserPasswordSettingTypeRandom.passwordSettingsPasswordCharacterSets.invalidFeedback', {ns: 'configuration'})}
                        invalid={props.validation.indexOf("passwordSettings.passwordCharacterSets") !== -1}
                    />
                </>);
            }
            default: return null;
        }
    }
   

    return (<Card className="mb-3">
        <CardHeader tag="h5">
            <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen(!isOpen)}>{isOpen ? '-' : '+'}</Button>
            {t('entraIdUserPasswordSettings.header', {ns: 'configuration'})}
        </CardHeader>
        <Collapse isOpen={isOpen}>
            <CardBody>
                <EnumInput
                    title={t('entraIdUserPasswordSettings.passwordSettingsType.title', {ns: 'configuration'})}
                    name="passwordSettings.type"
                    value={props.passwordSettings.type}
                    options={EntraId.passwordTypeOptions.map(key => ({
                        value: key,
                        text: t(`entraIdUserPasswordSettings.passwordSettingsType.options.${key}`, {ns: 'configuration'})    
                    }))}
                    onChange={(_, value) => updateConfig({ type: value })}
                    disabled={props.disabled}
                />

                <BoolInput
                    title={t('entraIdUserPasswordSettings.passwordSettingsForcePasswordChangeOnNextLogin.title', {ns: 'configuration'})}
                    name="passwordSettings.forcePasswordChangeOnNextLogin"
                    value={props.passwordSettings.forcePasswordChangeOnNextLogin}
                    disabled={props.disabled}
                    updateValue={(_, value) => updateConfig({ forcePasswordChangeOnNextLogin: value })}
                    titleSmall={true}
                />

                {renderTypeContent()}
            </CardBody>
        </Collapse>
    </Card>);
}

export default EntraIdUserUserPasswordSettings;
